import React, { useEffect, useState } from 'react';
/**
 * `DirectDebitSetup` captures and validates direct debit details for the dealer
 * journey.
 *
 * Behaviour:
 * - Renders the shared `DirectDebit` component and coordinates validate/update
 *   triggers.
 * - Enables the confirm button only when the child form indicates the user can
 *   continue.
 * - Branches forward navigation based on selected payment types (subscription /
 *   single payment -> single payment page; otherwise registers policy).
 * - Redirects to the confirmation page once `orderNumbers` are populated.
 */

import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import {
  PageAbsolutePath,
  PageRelativePath,
  ProductChannel,
  RegistrationState
} from '../../consts/Common';
import { DirectDebitDetails } from '../common/CommonTypes';
import { PaymentMethod } from '../common/Constants';
import { hasPaymentSelected } from '../common/ProductMethods';
import DirectDebit from '../common/DirectDebit/DirectDebit';
import PageHeader from '../common/PageHeader';
import { ButtonContinue } from '../common/Button/ButtonContinue';
import { ButtonBack } from '../common/Button/ButtonBack';
import { useJourney } from './Layout';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../app-state/Hooks';

export type DirectDebitErrors = {
  accountHolderName?: string;
  sortCode?: string;
  accountNumber?: string;
  bankValidation?: string;
  paymentDay?: string;
};

const DirectDebitSetup = (): JSX.Element => {
  const [goBack, setGoBack] = useState(false);
  const [canContinue, setCanContinue] = useState(false);
  const [validationInProgress, setValidationInProgress] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [updateDirectDebitDetails, setUpdateDirectDebitDetails] =
    useState(false);
  const [updateComplete, setUpdateComplete] = useState(false);
  const [goForward, setGoForward] = useState(false);
  const [redirect, setRedirect] = useState('');
  const [submissionInProgress, setSubmissionInProgress] = useState(false);

  const navigate = useNavigate();
  const userToken = useAppSelector((s) => s.user.token);
  const cms = useAppSelector((s) => s.content.directDebitPage);
  const cultureCode = useAppSelector((s) => s.appConfig.cultureCode);

  const { journey, setDirectDebitDetails, onRegister, onPageChange } =
    useJourney();

  const { directDebitDetails, orderNumbers, products, registrationState } =
    journey;

  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (redirect !== '') {
      navigate(redirect);
    }
  }, [redirect]);

  useEffect(() => {
    if (orderNumbers && orderNumbers.length > 0) {
      setRedirect(PageRelativePath.Confirmation);
    }
  }, [orderNumbers]);

  const hasSubscription = hasPaymentSelected(
    products,
    PaymentMethod.Subscription
  );

  const hasSinglePayment = products.some(
    (p) => p.selectedPaymentMethod === PaymentMethod.Single
  );

  useEffect(() => {
    if (goForward === true && updateComplete === true) {
      if (hasSubscription === true || hasSinglePayment === true) {
        setRedirect(PageRelativePath.SinglePayment);
      } else {
        onRegister('', '');
      }
    }
  }, [updateComplete]);

  useEffect(() => {
    if (goBack === true) {
      setRedirect(PageRelativePath.Summary);
    } else if (goForward === true) {
      if (hasSubscription !== true) {
        onPageChange(PageAbsolutePath.DirectDebit, true);
      }
    }
  }, [goForward, goBack]);

  const onBack = () => {
    setGoBack(true);
    onPageChange(PageAbsolutePath.DirectDebit, isValid);
  };

  const onContinue = () => {
    setGoForward(true);
    setValidationInProgress(true);
  };

  const onValidated = (valid: boolean) => {
    if (!valid) {
      setValidationInProgress(false);
    }

    setIsValid(valid);
  };

  const onUpdateDirectDebitDetails = (
    canContinueToNextPage: boolean,
    directDebit: DirectDebitDetails
  ) => {
    setCanContinue(canContinueToNextPage);
    setDirectDebitDetails(directDebit);
    if (updateDirectDebitDetails === true) {
      setUpdateDirectDebitDetails(false);
      setUpdateComplete(true);
    }
  };

  useEffect(() => {
    if (isValid === true) {
      setUpdateDirectDebitDetails(true);
      setUpdateComplete(false);
    }
  }, [isValid]);

  useEffect(() => {
    if (registrationState === RegistrationState.Registering) {
      setSubmissionInProgress(true);
    } else {
      setSubmissionInProgress(false);
    }
  }, [registrationState]);

  const displayCustomerFriendlyName = products.every(
    (p) =>
      p.productVariants &&
      p.productVariants.every((v) => v.customerFriendlyName)
  );

  return (
    <>
      <Helmet>
        <title>{t('page-title.direct-debit')}</title>
      </Helmet>

      <div>
        {!validationInProgress && (
          <PageHeader
            title={cms.title}
            description={cms.description}
            information={cms.information}
            warning={cms.warning}
            descriptionClass='p2'
          />
        )}

        <DirectDebit
          initialDirectDebitDetails={directDebitDetails}
          products={products}
          update={updateDirectDebitDetails}
          validate={validationInProgress}
          token={userToken}
          onValidated={onValidated}
          onUpdateDirectDebitDetails={onUpdateDirectDebitDetails}
          showLoading={submissionInProgress}
          directDebitPayee={cms.guaranteePayee}
          productChannel={ProductChannel.DealerJourney}
          culture={cultureCode}
          displayCustomerFriendlyName={displayCustomerFriendlyName}
        />
        {!validationInProgress && (
          <div className='flex-row form-nav'>
            <ButtonBack
              name='btnBack'
              onClick={onBack}
              text={t('customer-button.back-buttont-text')}
            />

            <ButtonContinue
              name='btnCheckout'
              text={t('customer-button.button-confirm-payment')}
              onClick={onContinue}
              disabled={!canContinue}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default DirectDebitSetup;
